import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn'

/**
 * A drawn signature for delivery proof (AF-017).
 *
 * A photo proves what was delivered; a signature proves who received it —
 * two different claims, so this is its own control rather than a second
 * {@link ImageUpload}. Exports the same shape ({@code File | null}) so a
 * caller treats both proofs identically once captured.
 */
export type SignaturePadProps = {
  label: string
  value: File | null
  onValueChange: (file: File | null) => void
  disabled?: boolean
}

export function SignaturePad({ label, value, onValueChange, disabled = false }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const [empty, setEmpty] = useState(true)

  // A cleared value from outside (e.g. the parent form resetting) must clear
  // the canvas too, or the pad would show a signature the caller no longer has.
  useEffect(() => {
    if (value === null) {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        setEmpty(true)
      }
    }
  }, [value])

  function point(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = event.currentTarget
    const rect = canvas.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    }
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return
    drawing.current = true
    const ctx = event.currentTarget.getContext('2d')
    const { x, y } = point(event)
    ctx?.beginPath()
    ctx?.moveTo(x, y)
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return
    const ctx = event.currentTarget.getContext('2d')
    if (!ctx) return
    const { x, y } = point(event)
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#0f172a'
    ctx.lineTo(x, y)
    ctx.stroke()
    setEmpty(false)
  }

  function end() {
    if (!drawing.current) return
    drawing.current = false
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.toBlob((blob) => {
      if (blob) onValueChange(new File([blob], 'signature.png', { type: 'image/png' }))
    }, 'image/png')
  }

  function clear() {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
    setEmpty(true)
    onValueChange(null)
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-slate-600">{label}</span>

      <canvas
        ref={canvasRef}
        role="img"
        aria-label={empty ? `${label} : vide` : `${label} : signée`}
        width={400}
        height={160}
        className={cn(
          'h-40 w-full touch-none rounded-md border border-dashed border-slate-400 bg-slate-100',
          disabled && 'pointer-events-none opacity-50',
        )}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />

      <button
        type="button"
        onClick={clear}
        disabled={disabled || empty}
        className="text-brand-600 self-start text-xs font-medium disabled:opacity-40"
      >
        Effacer
      </button>
    </div>
  )
}
